<template>
  <div class="container">
    <form @submit.prevent="createOperation">
      <h5>Adicionar nova operação</h5>
      <p class="account-name">{{ accountName }}</p>
      <label for="operation-type" class="operation-type-list"
        >Operação
        <ul id="operation-type" class="operation-type-list">
          <li class="operation-type-list-item" id="income" @click="selectOperationType">
            <AppIcon id="income" iconName="income" />
            entrada
          </li>
          <li class="operation-type-list-item" id="expense" @click="selectOperationType">
            <AppIcon id="expense" iconName="expense" />
            saída
          </li>
        </ul>
        <transition name="fade" mode="out-in">
        <small v-show="noOperationTypeSelected" class="message-error"
          >Escolha o tipo de operação.</small
        >
      </transition>
      </label>
      <label for="category-id"
        >Categoria
        <ul class="category-list">
          <li
            v-for="{ id, name } in selectedOperationCategories"
            :key="id"
            :id="id"
            class="category-list-item"
            @click="selectCategory"
          >
            {{ name }}
          </li>
        </ul>
        <transition>
          <small v-show="noOperationCategorySelected" class="message-error"
            >Escolha a categoria da operação.</small>
        </transition>
      </label>
      <label for="amount"
        >Montante
        <input
          @focus="clearErrors"
          v-model="operationAmount"
          type="number"
          inputmode="decimal"
          step="0.01"
          min="0"
          name="amount"
          id="amount"
          required
          autocomplete="transaction-amount"
        />
        <span v-if="isAmountNegative" class="error-message">
          Apenas montantes positivos são aceites.</span>
        <span v-if="invalidAmount" class="error-message">
          Montante inválido.</span>
      </label>
      <label class="remaining-balance">
        Montante restante
        <span>{{ remainingBalance }} {{ accountCurrency }}</span>
      </label>
      <label for="operationDate">
        Data
        <input v-model="operationDate" :max="operationDate" type="date" required />
      </label>
      <label for="description"
        >Detalhes da operação
        <input v-model="operationDescription" type="text" inputmode="text" autocomplete="off" />
      </label>
      <section class="action-buttons">
        <AppButton :text="textForButton" :disabled="isSaving"/>
        <p @click="goBack">Cancelar</p>
      </section>
    </form>
  </div>
</template>

<script>
import axios from "axios";
import { mapActions, mapGetters } from "vuex";
import AppButton from "../components/AppButton.vue";
import AppIcon from "../components/AppIcon.vue";
import { requestBuilder } from "../misc/util";

export default {
  name: "AddOperation",
  components: {
    AppButton,
    AppIcon
  },
  created() {
    this.getCategories();
    this.setAccountData();
    this.getCurrentDate();
  },
  data() {
    return {
      operationCategories: [],
      operationType: "",
      operationAmount: null,
      operationCategoryId: null,
      userOperationCategoryId: null,
      operationDescription: "",
      accountID: null,
      accountOwnerID: null,
      accountName: "",
      accountBalance: 0,
      accountCurrency: "",
      operationDate: null,
      noOperationTypeSelected: false,
      noOperationCategorySelected: false,
      invalidAmount: false,
      isSaving: false
    };
  },
  computed: {
    ...mapGetters(["getUserId", "isLoggedIn"]),
    selectedOperationCategories() {
      if (this.operationType === "") return this.operationCategories;
      return this.operationCategories.filter(o => o.category_type === this.operationType);
    },
    remainingBalance() {
      if (this.operationType === "income") {
        const result = Number(this.accountBalance) + Number(this.operationAmount);
        return result.toFixed(2);
      }
      const result = this.accountBalance - this.operationAmount;
      return result.toFixed(2);
    },
    isAmountNegative() {
      const amount = parseFloat(this.operationAmount);
      if (amount < 0) return true;
      return false;
    },
    textForButton() {
      if (this.isSaving) return "Adicionando...";
      return "Adicionar Operação";
    }
  },
  methods: {
    ...mapActions(["changeUserId"]),
    setAccountData() {
      if (this.isLoggedIn) {
        this.accountID = this.$route.params.accountData.id;
        this.accountOwnerID = this.$route.params.accountData.user_id;
        this.accountName = this.$route.params.accountData.account_name;
        this.accountBalance = this.$route.params.accountData.current_balance.toFixed(2);
        this.accountCurrency = this.$route.params.accountData.currency.short_name;
      } else this.$router.push({ name: "accounts" });
    },
    async getCategories() {
      const url = "/operation-categories";
      const method = "GET";
      const requestOptions = requestBuilder(url, method);

      try {
        const { data } = await axios.request(requestOptions);
        this.operationCategories = data;
      } catch (error) {
        console.log(error);
      }
    },
    requestData() {
      const data = {
        accountId: this.accountID,
        operationCategoryId: Number(this.operationCategoryId),
        userOperationCategoryId: null,
        operationType: this.operationType,
        currency: this.accountCurrency,
        amount: parseFloat(this.operationAmount),
        description: this.operationDescription,
        effectiveBy: this.operationDate
      };

      return data;
    },
    clearErrors() {
      this.invalidAmount = false;
    },
    setErrorMessages() {
      this.invalidAmount = true;
    },
    async createOperation() {
      if (this.isAmountNegative === false) {
        const navigatorIsFirefox = () => {
          if (navigator.userAgent.indexOf("Firefox") > -1) return true;
          return false;
        };

        if (this.operationType === "") {
          this.noOperationTypeSelected = true;
          const operationElements = document.querySelectorAll("h5");

          if (navigatorIsFirefox) {
            operationElements[0].scrollIntoView({ behavior: "smooth" });
          }
          operationElements[0].scrollIntoViewIfNeeded(true);
        } else if (this.operationCategoryId === null) {
          this.noOperationCategorySelected = true;
          const element = document.querySelector("h5");

          if (navigatorIsFirefox) {
            element.scrollIntoView({ behavior: "smooth" });
          }
          element.scrollIntoViewIfNeeded(true);
        } else {
          this.isSaving = true;
          const url = "/operations";
          const method = "POST";
          const operationData = this.requestData();
          const requestOptions = requestBuilder(url, method, operationData);

          try {
            const { data } = await axios.request(requestOptions);
            this.$route.params.accountData.operations = data.operations;
            this.$route.params.accountData.updated_at = data.updated_at;
            this.$route.params.accountData.current_balance = data.current_balance;
            this.$router.push({ name: "accountTransactions",
              params: { accountID: this.accountID, accountData: this.$route.params.accountData } });
          } catch (error) {
            if (error.response.data.error === "impossible") this.setErrorMessages();
            else console.log({ error });
          }
          this.isSaving = false;
        }
      }
    },
    selectOperationType(e) {
      this.noOperationTypeSelected = false;
      const { id } = e.srcElement;
      this.operationType = id;

      const element = document.querySelector(`#${CSS.escape(id)}`);
      const elementIcon = document.querySelector(`#${CSS.escape(id)} svg`);
      const selectedElement = document.querySelector(".selected-operation-type");
      const selectedElementIcon = document.querySelector("svg.selected-operation-type");

      if (selectedElement !== null) {
        selectedElement.classList.toggle("selected-operation-type");
        selectedElementIcon.classList.toggle("selected-operation-type");

        element.classList.toggle("selected-operation-type");
        elementIcon.classList.toggle("selected-operation-type");
      } else {
        elementIcon.classList.toggle("selected-operation-type");
        element.classList.toggle("selected-operation-type");
      }
    },
    selectCategory(e) {
      this.noOperationCategorySelected = false;
      if (this.operationType !== "") {
        const selectedItem = document.querySelector(".selected-category-list-item");
        const { id: targetId } = e.srcElement;
        this.operationCategoryId = targetId;

        if (selectedItem !== null) {
          selectedItem.classList.toggle("selected-category-list-item");

          const element = document.querySelector(`#${CSS.escape(targetId)}`);
          element.classList.toggle("selected-category-list-item");
        } else {
          const element = document.querySelector(`#${CSS.escape(targetId)}`);
          element.classList.toggle("selected-category-list-item");
        }
      } else this.noOperationTypeSelected = true;
    },
    getCurrentDate() {
      const year = new Date().getFullYear();
      const month = `0${new Date().getMonth() + 1}`;
      const day = () => {
        const tempDay = new Date().getDate();
        if (tempDay < 10) return `0${tempDay}`;
        return tempDay;
      };
      if (month.length === 3) {
        const fixedMonth = month.slice(1, 3);
        this.operationDate = `${year}-${fixedMonth}-${day()}`;
      } else this.operationDate = `${year}-${month}-${day()}`;
    },
    goBack() {
      const nextRoutePath = `/contas/${this.$route.params.accountID}/transaccoes`;
      const currentRoutePath = this.$route.path;
      if (nextRoutePath !== currentRoutePath) {
        this.$router.push({
          name: "accountTransactions",
          params: { accountData: this.$route.params.accountData }
        });
      }
    }
  }
};
</script>

<style scoped>
label {
  color: var(--color-tertiary);
  margin-bottom: 1.6rem;
}

.account-name {
  color: var(--color-secondary);
  font-size: 0.75rem;
  margin-bottom: 1.6rem;
}

.container {
  display: flex;
  flex-direction: column;
}

.container form {
  width: 100%;
  margin-bottom: 1.6rem;
}

.operation-type-list {
  display: flex;
  flex: 0 0 100%;
}

.operation-type-list-item {
  background-color: var(--color-white);
  color: var(--color-primary);
  border-radius: 10px;
  padding: 4px 6px 4px 4px;
  display: flex;
  align-items: center;
  min-width: fit-content;
  flex-wrap: wrap;
  margin-right: 1rem;
}

.operation-type-list li:last-child {
  padding-right: 8px;
}

.operation-type-list-item svg {
  margin-right: 6px;
}

.selected-operation-type {
  background-color: var(--color-primary);
  color: var(--color-quinary);
}

svg {
  fill: var(--color-primary);
}

svg.selected-operation-type {
  fill: var(--color-quinary);
}

.category-list {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.8rem;
}

.category-list-item {
  background-color: var(--color-white);
  color: var(--color-tertiary);
  border-radius: 10px;
  padding: 4px;
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
}

.selected-category-list-item {
  background-color: var(--color-secondary);
  color: var(--color-white);
}

.remaining-balance span {
  font-size: 1rem;
  color: var(--color-primary);
}

.action-buttons {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.action-buttons button {
  background-color: var(--color-primary);
  color: var(--color-quaternary);
  margin-bottom: 1.6rem;
}

.action-buttons p {
  color: var(--color-secondary);
  margin-bottom: 1.6rem;
  cursor: pointer;
}

.message-error {
  margin-top: 0.3em;
  font-size: 0.833rem;
  color: var(--color-senary);
}
</style>
